import { resolveVenue } from '../../config/venues.jsx';
import { WEDDING_TZ } from '../../config/time.js';

// The wedding's timezone now lives in src/config/time.js, so that anything
// needing it doesn't have to import this module and drag the venue registry
// along with it. Imported and re-exported here because this is where callers
// already look for it, and because they surface a footnote when the viewer's
// zone differs.
export { WEDDING_TZ };

export function viewerTzDiffers() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone !== WEDDING_TZ;
  } catch {
    return false;
  }
}

// Parses the ICS UTC basic format ('YYYYMMDDTHHMMSSZ') that both the calendar
// function and src/utils/calendar.jsx speak.
function parseIcsUtc(value) {
  const m = /^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z$/.exec(value || '');
  if (!m) return null;
  const [, y, mo, d, h, mi, s] = m;
  return new Date(Date.UTC(+y, +mo - 1, +d, +h, +mi, +s));
}

export function formatTime(date) {
  return date.toLocaleTimeString([], {
    hour: 'numeric',
    minute: '2-digit',
    timeZone: WEDDING_TZ,
  });
}

export function formatDayLong(date) {
  return date.toLocaleDateString([], {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    timeZone: WEDDING_TZ,
  });
}

// 'YYYY-MM-DD' for the given instant *as seen in the wedding's timezone*, so
// grouping never splits a day at the viewer's midnight.
export function dayKey(date) {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: WEDDING_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(date);
  const map = {};
  for (const p of parts) if (p.type !== 'literal') map[p.type] = p.value;
  return `${map.year}-${map.month}-${map.day}`;
}

const DAY_MS = 24 * 60 * 60 * 1000;

// Splits the flat event list into per-day buckets. All-day events become the
// day's "chapters" (its theme) rather than entries in the running order, and
// an all-day event spanning several days themes each one.
export function groupByDay(events) {
  const days = new Map();

  const ensureDay = (key, date) => {
    if (!days.has(key)) days.set(key, { key, date, chapters: [], timed: [] });
    return days.get(key);
  };

  for (const event of events) {
    const start = parseIcsUtc(event.startUtc);
    if (!start) continue;
    const end = parseIcsUtc(event.endUtc) || start;
    const venue = resolveVenue(event.location);
    const enriched = { ...event, start, end, venue };

    if (event.allDay) {
      // DTEND is exclusive for all-day events, so walk [start, end) by day —
      // and always cover at least the start day when DTEND is missing/equal.
      let cursor = start.getTime();
      const stop = Math.max(end.getTime(), start.getTime() + 1);
      let guard = 0;
      while (cursor < stop && guard < 40) {
        const at = new Date(cursor);
        ensureDay(dayKey(at), at).chapters.push(enriched);
        cursor += DAY_MS;
        guard += 1;
      }
    } else {
      ensureDay(dayKey(start), start).timed.push(enriched);
    }
  }

  const ordered = [...days.values()].sort((a, b) => a.key.localeCompare(b.key));
  for (const day of ordered) {
    day.timed.sort((a, b) => a.start - b.start);
    // Only top-level events go on the spine; sub-events render inside their
    // parent's card. `timed` stays the full list so counts and the now/next
    // marks still see everything.
    day.clusters = clusterOverlapping(assignNesting(day.timed));
  }
  return ordered;
}

// A sub-event has to be meaningfully shorter than the thing it sits inside,
// or two near-identical overlapping events (the bride's and groom's
// preparations, say) would read as one nested in the other rather than as
// the parallel events they are.
const MAX_CHILD_SHARE = 0.7;

// Google Calendar has no parent/child concept, so nesting is inferred from
// the times: an event sitting entirely inside a longer one at the same venue
// becomes its child — the steps within a dinner, say. Requiring the same
// venue and a meaningfully shorter duration is what keeps genuinely parallel
// events (the bride's and groom's preparations, in different places) from
// being read as one nested inside the other.
//
// Attaches `children` to each parent and returns only the top-level events.
// Deliberately one level deep: a grandchild re-points to its top-level
// ancestor rather than nesting further, which keeps the rendering simple and
// is plenty for a wedding running order.
function assignNesting(timed) {
  const parentOf = new Map();
  const duration = (e) => e.end.getTime() - e.start.getTime();

  for (const event of timed) {
    // Smallest containing candidate wins, so the deepest sensible parent is
    // chosen when several enclose the same event.
    let best = null;
    for (const candidate of timed) {
      if (candidate === event) continue;
      if (candidate.venue?.id !== event.venue?.id) continue;
      if (duration(event) > duration(candidate) * MAX_CHILD_SHARE) continue;
      if (event.start < candidate.start || event.end > candidate.end) continue;
      if (!best || duration(candidate) < duration(best)) best = candidate;
    }
    if (best) parentOf.set(event, best);
  }

  // Flatten to a single level. A parent is always strictly longer than its
  // child, so walking up this chain always terminates.
  const topLevel = [];
  for (const event of timed) {
    let parent = parentOf.get(event);
    while (parent && parentOf.get(parent)) parent = parentOf.get(parent);
    if (!parent) {
      event.children = event.children || [];
      topLevel.push(event);
    } else {
      parent.children = parent.children || [];
      parent.children.push(event);
    }
  }
  for (const event of topLevel) event.children.sort((a, b) => a.start - b.start);
  return topLevel;
}

// Groups events that overlap in time, so layouts can render simultaneous
// things side by side instead of pretending the day is strictly sequential.
function clusterOverlapping(timed) {
  const clusters = [];
  for (const event of timed) {
    const open = clusters[clusters.length - 1];
    const overlapsOpen =
      open && open.some((e) => event.start < e.end && event.end > e.start);
    if (overlapsOpen) open.push(event);
    else clusters.push([event]);
  }
  return clusters;
}

export function isSameDayAsNow(day, now) {
  return day.key === dayKey(new Date(now));
}

export function eventState(event, now) {
  if (now >= event.end.getTime()) return 'past';
  if (now >= event.start.getTime()) return 'now';
  return 'future';
}

export function eventProgress(event, now) {
  const span = event.end.getTime() - event.start.getTime();
  if (span <= 0) return 0;
  return Math.min(1, Math.max(0, (now - event.start.getTime()) / span));
}

// The "what's happening / what's next" summary that drives the on-card
// Now / Up next marks.
export function nowSummary(days, now) {
  const all = days.flatMap((d) => d.timed);
  const current = all.find((e) => now >= e.start.getTime() && now < e.end.getTime());
  if (current) return { kind: 'now', event: current };
  const next = all.find((e) => e.start.getTime() > now);
  if (next) return { kind: 'next', event: next };
  if (all.length) return { kind: 'done' };
  return null;
}

// The span the schedule actually covers, for the line under the title. Reads
// as one date when everything happens on a single day.
export function scheduleRange(days) {
  if (!days.length) return '';
  const first = formatDayLong(days[0].date);
  if (days.length === 1) return first;
  return `${first} – ${formatDayLong(days[days.length - 1].date)}`;
}
